import { useEffect, useRef, useState } from 'react';
import { wechatApi } from '../api';

export type Purpose = 'login' | 'bind';
type Flow = { state: string; verifier: string; deadline: number; purpose: Purpose };
export type NativeResult = Partial<Flow> & { status?: string; code?: string; message?: string };
export interface NativeWechat {
  authorize: (options: { appId: string; state: string; verifier: string; purpose: Purpose }) => Promise<unknown>;
  getResult: () => Promise<NativeResult>;
  clearResult: (options: { state: string }) => Promise<unknown>;
  addListener: (event: 'wechatAuthResult', callback: (data: NativeResult) => void) => Promise<{ remove: () => Promise<void> }>;
}
const STORAGE_KEY = 'xbb_wechat_mp_flow_v2';
export function createVerifier(): string {
  const bytes = new Uint8Array(32);
  if (!window.crypto?.getRandomValues) throw new Error('请使用安全连接或更新 APP 后重试微信登录');
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}
function safeAuthorizationUrl(raw: string, channel: string): string {
  const url = new URL(raw);
  const path = channel === 'web' ? '/connect/qrconnect' : '/connect/oauth2/authorize';
  if (url.protocol !== 'https:' || url.hostname !== 'open.weixin.qq.com' || url.pathname !== path) {
    throw new Error('微信授权地址无效，请稍后重试');
  }
  return url.toString();
}

export function useWechatLogin(options: {
  onSuccess: (data: any) => void; purpose: Purpose; native?: NativeWechat;
}) {
  const [available, setAvailable] = useState(false);
  const [configurationMessage, setConfigurationMessage] = useState('正在检查微信登录…');
  const [phase, setPhase] = useState<'idle' | 'starting' | 'waiting' | 'processing'>('idle');
  const [message, setMessage] = useState('');
  const [authorizationUrl, setAuthorizationUrl] = useState('');
  const [seconds, setSeconds] = useState(0);
  const onSuccess = useRef(options.onSuccess);
  onSuccess.current = options.onSuccess;
  const flow = useRef<Flow | null>(null);
  const epoch = useRef(0);
  const mounted = useRef(false);
  const channel = options.native ? 'mobile' : (/MicroMessenger/i.test(navigator.userAgent) ? 'mp' : 'web');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leavingForMp = useRef(false);
  const completing = useRef('');
  const ignored = useRef(new Set<string>());

  function stopTimer() {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }
  function clearMp() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* optional storage */ }
  }
  async function discard(previous: Flow | null) {
    if (!previous) return;
    await Promise.allSettled([
      wechatApi.cancelLogin(previous.state, previous.verifier),
      ...(options.native ? [options.native.clearResult({ state: previous.state })] : []),
    ]);
  }
  function cancel(text = '已取消微信授权，可以重新登录') {
    (epoch.current += 1);
    stopTimer();
    const previous = flow.current;
    if (previous) ignored.current.add(previous.state);
    flow.current = null;
    clearMp();
    void discard(previous);
    if (mounted.current) {
      setPhase('idle'); setAuthorizationUrl(''); setMessage(text);
    }
  }
  function success(data: any, attempt: number) {
    if (!mounted.current || attempt !== epoch.current || !flow.current) return;
    const previous = flow.current;
    if (previous) ignored.current.add(previous.state);
    flow.current = null;
    (epoch.current += 1);
    stopTimer(); clearMp();
    if (options.native) void options.native.clearResult({ state: previous.state }).catch(() => {});
    setPhase('idle'); setAuthorizationUrl(''); setMessage('');
    onSuccess.current(data);
  }
  function waitForResult(attempt: number) {
    stopTimer();
    const poll = async () => {
      const current = flow.current;
      if (!mounted.current || attempt !== epoch.current || !current) return;
      const remaining = Math.ceil((current.deadline - Date.now()) / 1000);
      setSeconds(Math.max(0, remaining));
      if (remaining <= 0) { cancel('微信授权已超时，请重新登录'); return; }
      try {
        if (options.native) {
          const result = await options.native.getResult();
          if (!mounted.current || attempt !== epoch.current) return;
          if (result.status && result.status !== 'pending') { void handleNativeResult(result); return; }
        } else {
        const response = await wechatApi.checkLogin(current.state, current.verifier);
        if (!mounted.current || attempt !== epoch.current || flow.current !== current) return;
        if (response.data.status === 'confirmed') { success(response.data, attempt); return; }
        if (['expired', 'cancelled', 'error', 'consumed'].includes(response.data.status)) {
          cancel(response.data.message || '微信授权已结束，请重新登录'); return;
        }
        }
      } catch (error: any) {
        if (!mounted.current || attempt !== epoch.current) return;
        if ([400, 403, 404, 409].includes(error.response?.status)) {
          cancel(error.response?.data?.detail || '微信登录会话已失效，请重试'); return;
        }
        setMessage('网络暂时不可用，正在重试；也可以取消后使用账号密码登录');
      }
      if (mounted.current && attempt === epoch.current) timer.current = setTimeout(poll, 1500);
    };
    timer.current = setTimeout(poll, 0);
  }
  async function handleNativeResult(result: NativeResult) {
    if (!mounted.current || !result.state || !result.verifier || ignored.current.has(result.state) || result.purpose !== options.purpose) return;
    if (flow.current && flow.current.state !== result.state) return;
    if (!flow.current) {
      flow.current = { state: result.state, verifier: result.verifier, purpose: options.purpose,
        deadline: result.deadline || Date.now() };
    }
    if (result.status === 'pending') {
      setPhase('waiting'); waitForResult(epoch.current); return;
    }
    if (result.status !== 'success' || !result.code) {
      cancel(result.message || (result.status === 'expired' ? '微信授权已超时，请重试' : '已取消微信授权'));
      return;
    }
    if (completing.current === result.state) return;
    completing.current = result.state;
    stopTimer();
    setPhase('processing');
    const attempt = epoch.current;
    try {
      const response = await wechatApi.completeNativeLogin(result.state, result.verifier, result.code);
      success(response.data, attempt);
    } catch (error: any) {
      if (mounted.current && attempt === epoch.current) cancel(error.response?.data?.detail || '微信登录未完成，请重试');
    } finally {
      if (completing.current === result.state) completing.current = '';
    }
  }

  useEffect(() => {
    mounted.current = true;
    (epoch.current += 1);
    let removed = false;
    let listener: { remove: () => Promise<void> } | undefined;
    wechatApi.getConfig().then(response => {
      if (removed) return;
      const config = response.data.channels?.[channel];
      setAvailable(!!config?.configured);
      setConfigurationMessage(config?.configured ? '' : (config?.message || '微信登录服务待配置，请使用账号密码登录'));
    }).catch(() => {
      if (!removed) setConfigurationMessage('暂时无法检查微信登录，请稍后重试或使用账号密码登录');
    });
    if (options.native) {
      const native = options.native;
      // Install the listener before reading the retained result to close the cold-start race.
      native.addListener('wechatAuthResult', data => { void handleNativeResult(data); }).then(async handle => {
        if (removed) { await handle.remove(); return; }
        listener = handle;
        const pending = await native.getResult();
        if (!removed) void handleNativeResult(pending);
      }).catch(() => {
        if (!removed) cancel('当前 APP 的微信登录组件不可用，请更新 APP 或使用账号密码登录');
      });
    } else if (channel === 'mp') {
      try {
        const previous: Flow = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
        if (previous?.state && previous?.verifier && previous.purpose === options.purpose) {
          flow.current = previous; setPhase('waiting'); waitForResult(epoch.current);
        }
      } catch { clearMp(); }
    }
    return () => {
      removed = true; mounted.current = false; (epoch.current += 1); stopTimer();
      if (listener) void listener.remove();
      const previous = flow.current; flow.current = null;
      if (!leavingForMp.current) { clearMp(); void discard(previous); }
    };
    // Flow helpers access refs; the authorization owner only changes with channel/purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, options.native, options.purpose]);

  async function start() {
    if (!available || phase !== 'idle') return;
    const attempt = (epoch.current += 1);
    setPhase('starting'); setMessage(''); setAuthorizationUrl('');
    let started: Flow | null = null;
    try {
      const verifier = createVerifier();
      const response = await wechatApi.startLogin(channel, verifier, options.purpose, channel === 'mp' ? window.location.origin : undefined);
      started = { state: response.data.state, verifier, purpose: options.purpose,
        deadline: Date.now() + response.data.expires_in * 1000 };
      if (!mounted.current || attempt !== epoch.current) { void discard(started); return; }
      flow.current = started;
      setSeconds(response.data.expires_in);
      if (options.native) {
        await options.native.authorize({ appId: response.data.app_id, state: started.state, verifier, purpose: options.purpose });
        if (!mounted.current || attempt !== epoch.current) { void discard(started); return; }
      } else {
        const url = safeAuthorizationUrl(response.data.authorization_url, channel);
        if (channel === 'mp') {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(started));
          leavingForMp.current = true;
          window.location.assign(url);
          return;
        }
        setAuthorizationUrl(url);
      }
      setPhase('waiting'); waitForResult(attempt);
    } catch (error: any) {
      if (mounted.current && attempt === epoch.current) cancel(error.response?.data?.detail || error.message || '微信登录启动失败，请重试');
      else void discard(started);
    }
  }
  return { available, configurationMessage, phase, message, authorizationUrl, seconds,
    busy: phase !== 'idle', start, cancel: () => cancel() };
}
